/**
 * The diner's path at `/order?site=<uuid>` (ORDR-42).
 *
 * ANONYMOUS THROUGHOUT. The flow is `GET /api/v1/ordering/flow/?entry=&site=`,
 * which answers with no session (ORDR-53); the cart is commerce's anonymous
 * FOOD cart behind its secret (COMM-132). Nothing here reads the ordering
 * context or the session, and the staff "not set up to order" notice is never
 * rendered on this path.
 *
 * ENTRY. `?entry=` from the address when it is present, else `BRAND_DOMAIN`.
 * The entry point is an arrival fact, not the fulfilment mode or the channel,
 * both of which come only from the resolver's answer.
 *
 * BRAND AND LOCATION (ORDR-118). After the chooser, the address carries
 * `brand` and `location` beside `site`, and both are sent to the resolver,
 * which validates them against the Site. Absent, the query is exactly
 * `entry` and `site`, as before.
 *
 * PLACEMENT (ORDR-118). The storefront's latest #2 answer and the flow's
 * `data.intake` both feed the services' `placementGuard`, read at the moment
 * of placement through a gate held outside render, so the guard sees the answer the diner is looking
 * at without rebuilding the services (and losing the cart) when it changes.
 *
 * CHECKOUT (ORDR-166). What the app's own Checkout is told: the modes the
 * LOCATION declares (its published schedule, or the resolver's one mode when
 * the MODE step was SKIPPED), the resolver's mode to preselect, and the
 * readiness estimate. The card's tokeniser is no longer built here: it is
 * built only once commerce refuses pay in store (`checkout/useCheckout.ts`).
 *
 * REFUSED SESSION (ORDR-219). Anonymous is the design, but the browser still
 * carries any session it holds, and commerce refuses the menu read and the
 * cart create with a 403 when that session's firm is not the site's. Either
 * refusal is `session-refused`: no services, no menu, and the diner is told
 * why and offered signing out (`CustomerStatus.tsx`). It is never an empty
 * menu, and never a total that silently cannot be worked out.
 */

import { useEffect, useMemo, useState } from "react";
import type { FinixCardConfig } from "@pirouz-ca/payments-core";
import type { ResolvedFlow } from "@pirouz-ca/ui-components";
import type { ApiClient } from "../api/client.js";
import { offeredModes } from "../checkout/answers.js";
import { cardConfigFor, type FinixCardPlatform } from "../config/env.js";
import { useStorefront, type Storefront } from "../storefront/useStorefront.js";
import type { CartSecretStore } from "./cartSecretStore.js";
import { createCommerceClient } from "./commerceClient.js";
import { createCustomerServices, type CustomerServices } from "./customerServices.js";
import { flowFromWire, type WireFlow } from "./flowFromWire.js";
import { readSiteMenu, type SiteMenu } from "./menuPayment.js";
import { dinerLanguage } from "./publicMenu.js";
import type { CatalogueSection } from "../order/catalogue.js";

export const DEFAULT_ENTRY = "BRAND_DOMAIN";

const NO_CATALOGUE: readonly CatalogueSection[] = [];

export type CustomerOrderingStatus =
  | { readonly kind: "loading" }
  | { readonly kind: "not-found" }
  | { readonly kind: "failed"; readonly correlationId: string | null }
  /** Commerce refused this session the menu or the cart (403, ORDR-219). */
  | { readonly kind: "session-refused"; readonly correlationId: string }
  | { readonly kind: "ready" };

export interface CheckoutFacts {
  /** Only the modes the location declares. */
  readonly modes: readonly string[];
  /** The resolver's `fulfilment_mode`, or `null` while MODE is REQUIRED. */
  readonly initialMode: string | null;
  /** The readiness estimate in whole minutes, rounded up; `null` when there is none. */
  readonly readyInMinutes: number | null;
}

export interface CustomerOrdering {
  readonly status: CustomerOrderingStatus;
  readonly flow: ResolvedFlow | undefined;
  readonly services: CustomerServices | undefined;
  /** The menu as the app's own Menu and Item screens read it (ORDR-165); `[]` until served. */
  readonly catalogue: readonly CatalogueSection[];
  /**
   * The hosted card form's config for THIS location (ORDR-57): the platform
   * from build env plus the firm's merchant id from the server. `null` when
   * either is missing. Used only after commerce refuses pay in store.
   */
  readonly cardConfig: FinixCardConfig | null;
  readonly disclosure: string | null;
  /** The flow's `data.intake`, verbatim; `null` when the flow carried none. */
  readonly intake: Intake | null;
  readonly checkout: CheckoutFacts;
  /** The location's hours, windows, schedule and readiness (ORDR-118). */
  readonly storefront: Storefront;
  /** Why placement would be refused now, or `null`. Browsing is never refused. */
  readonly placementRefusal: string | null;
}

export interface Intake {
  readonly accepting: boolean;
  readonly reason: string | null;
}

export interface UseCustomerOrderingOptions {
  readonly client: ApiClient;
  readonly siteId: string;
  readonly entry?: string;
  /** The brand from the address, after the chooser. Sent to the resolver as `brand`. */
  readonly brandNodeId?: string;
  /** The location from the address, after the chooser. Sent to the resolver as `location`. */
  readonly locationNodeId?: string;
  readonly secrets: CartSecretStore;
  readonly currency: string;
  /**
   * The platform half of card capture (ORDR-57). Card payment is possible only
   * once the menu for this site and location names the firm's merchant.
   */
  readonly cardPlatform?: FinixCardPlatform;
}

const INTAKE_REFUSAL = "This location is not taking new orders right now, so this order cannot be placed. Nothing has been charged.";

/**
 * `data.intake`, read from the raw body because `flowFromWire` is not this
 * lane's to change. Absent is `null`; present and malformed is a fault, named.
 */
function intakeFromWire(body: unknown): { ok: true; intake: Intake | null } | { ok: false } {
  const data = (body as { data?: unknown } | null)?.data as Record<string, unknown> | null | undefined;
  if (data === null || data === undefined || !Object.prototype.hasOwnProperty.call(data, "intake")) {
    return { ok: true, intake: null };
  }
  const raw = data["intake"] as Record<string, unknown> | null;
  if (raw === null) {
    return { ok: true, intake: null };
  }
  const accepting = typeof raw === "object" ? raw["accepting"] : undefined;
  const reason = typeof raw === "object" ? raw["reason"] : undefined;
  if (typeof accepting !== "boolean" || !(reason === null || typeof reason === "string")) {
    console.warn("[ordering] the flow's data.intake is not { accepting: boolean, reason: string | null }");
    return { ok: false };
  }
  return { ok: true, intake: { accepting, reason } };
}

/**
 * The latest refusal, held outside render: `set` from an effect, `check` when a
 * payment is attempted. One per hook instance, so the services built once keep
 * reading the answer the diner is looking at.
 */
interface PlacementGate {
  set(refusal: string | null): void;
  check(): string | null;
}

function createPlacementGate(): PlacementGate {
  let current: string | null = null;
  return {
    set(refusal) {
      current = refusal;
    },
    check: () => current,
  };
}

type Loaded =
  | { readonly kind: "ready"; readonly wire: WireFlow; readonly intake: Intake | null }
  | { readonly kind: "not-found" }
  | { readonly kind: "failed"; readonly correlationId: string | null };

export function useCustomerOrdering({
  client,
  siteId,
  entry = DEFAULT_ENTRY,
  brandNodeId,
  locationNodeId,
  secrets,
  currency,
  cardPlatform,
}: UseCustomerOrderingOptions): CustomerOrdering {
  const [loaded, setLoaded] = useState<{ key: string; value: Loaded } | null>(null);
  const key = `${entry}|${siteId}|${brandNodeId ?? ""}|${locationNodeId ?? ""}`;

  useEffect(() => {
    let live = true;
    const query = new URLSearchParams({ entry, site: siteId });
    if (brandNodeId !== undefined) {
      query.set("brand", brandNodeId);
    }
    if (locationNodeId !== undefined) {
      query.set("location", locationNodeId);
    }
    void client.request(`/ordering/flow/?${query.toString()}`).then((response) => {
      if (!live) {
        return;
      }
      let value: Loaded;
      if (response.status === 200) {
        const wire = flowFromWire(response.body);
        const intake = intakeFromWire(response.body);
        value =
          wire === null || !intake.ok
            ? { kind: "failed", correlationId: response.correlationId }
            : { kind: "ready", wire, intake: intake.intake };
      } else if (response.status === 404) {
        value = { kind: "not-found" };
      } else {
        value = { kind: "failed", correlationId: response.correlationId };
      }
      setLoaded({ key, value });
    });
    return () => {
      live = false;
    };
  }, [client, entry, siteId, brandNodeId, locationNodeId, key]);

  const current = loaded !== null && loaded.key === key ? loaded.value : null;
  const wire = current?.kind === "ready" ? current.wire : null;
  const intake = current?.kind === "ready" ? current.intake : null;

  const storefront = useStorefront({
    client,
    locationNodeId: wire?.fulfilment?.location_node_id ?? null,
    mode: wire?.fulfilment?.fulfilment_mode ?? null,
  });
  const placementRefusal =
    storefront.placementRefusal ?? (intake !== null && !intake.accepting ? INTAKE_REFUSAL : null);
  const [gate] = useState(createPlacementGate);
  useEffect(() => {
    gate.set(placementRefusal);
  }, [gate, placementRefusal]);

  // ORDR-60: ONE read of commerce's public menu for this site AND location
  // serves both the menu the diner orders from and, ORDR-57, the merchant the
  // card form opens against (never from the build or the browser; held in
  // state only). Read whether or not card payment is configured: the menu is
  // needed either way.
  //
  // ORDR-227: READ AT ONCE WHEN THE ADDRESS ALREADY SAYS WHERE. While the flow
  // is out, the menu is read for the site and location in the address, beside
  // the flow rather than after it. The flow answer stays authoritative: once it
  // is in, the read is for ITS site and location. When those match the
  // address, the target key is unchanged, so the read in flight (or already
  // answered) is the one served and nothing is read twice. When they differ,
  // the early read is dropped (its answer is keyed to the address, never to
  // `menuKey`, so it is never drawn) and the flow's location is read. With no
  // location in the address there is no early target, and the menu is read
  // after the flow exactly as before. The resolver refuses a location that
  // does not trade from the site rather than substituting another, so in
  // practice the second read is for a differently spelled id, not a different
  // place; the rule holds either way.
  const siteForMenu = wire?.fulfilment?.site_id ?? null;
  const locationForMenu = wire?.fulfilment?.location_node_id ?? null;
  const menuKey = siteForMenu === null || locationForMenu === null ? null : `${siteForMenu}|${locationForMenu}`;
  const earlyTarget = current === null && locationNodeId !== undefined ? { site: siteId, location: locationNodeId } : null;
  const flowTarget = siteForMenu === null || locationForMenu === null ? null : { site: siteForMenu, location: locationForMenu };
  const menuTarget = current === null ? earlyTarget : flowTarget;
  const targetKey = menuTarget === null ? null : `${menuTarget.site}|${menuTarget.location}`;
  const [siteMenu, setSiteMenu] = useState<{ key: string; value: SiteMenu } | null>(null);

  useEffect(() => {
    if (targetKey === null) {
      return;
    }
    const [targetSite = "", targetLocation = ""] = targetKey.split("|");
    let live = true;
    void readSiteMenu(client, targetSite, targetLocation, dinerLanguage()).then((value) => {
      if (live) {
        setSiteMenu({ key: targetKey, value });
      }
    });
    return () => {
      live = false;
    };
  }, [client, targetKey]);

  const served = menuKey !== null && siteMenu !== null && siteMenu.key === menuKey ? siteMenu.value : null;
  // ORDR-219: a 403 on the cart create or a line add, reported by the services.
  const [cartRefused, setCartRefused] = useState<string | null>(null);
  const refusedReference = served?.refused?.correlationId ?? cartRefused;
  // Services are built once the menu answer is in, so a late answer never
  // rebuilds them (and loses the cart) under a diner who has started ordering.
  const menuPending = menuKey !== null && served === null;
  const menuPayment = cardPlatform === undefined ? null : (served?.payment ?? null);
  const merchantId = menuPayment?.kind === "card" ? menuPayment.merchantId : null;
  const cardConfig = useMemo<FinixCardConfig | null>(
    () => (cardPlatform === undefined || merchantId === null ? null : cardConfigFor(cardPlatform, merchantId)),
    [cardPlatform, merchantId],
  );

  const built = useMemo(() => {
    if (wire === null || wire.fulfilment === null || served === null) {
      return null;
    }
    return createCustomerServices({
      commerce: createCommerceClient(client, secrets),
      fulfilment: wire.fulfilment,
      currency,
      placementGuard: gate.check,
      onCartRefused: setCartRefused,
    });
  }, [wire, client, secrets, currency, served, gate]);

  const schedule = storefront.schedule?.kind === "ok" ? storefront.schedule.schedule : null;
  const readiness = storefront.readiness?.kind === "ok" ? storefront.readiness.readiness : null;
  const resolvedMode = wire?.fulfilment?.fulfilment_mode ?? null;
  const modeSettled = wire?.flow.steps.some((step) => step.id === "MODE" && step.state === "SKIPPED") ?? false;
  const modes = useMemo(
    () => offeredModes({ declared: schedule === null ? null : schedule.modes.map((entry) => entry.mode), resolved: resolvedMode, settled: modeSettled }),
    [schedule, resolvedMode, modeSettled],
  );
  const readySeconds = readiness?.ready_in_seconds ?? null;
  const checkout = useMemo<CheckoutFacts>(
    () => ({ modes, initialMode: resolvedMode, readyInMinutes: readySeconds === null ? null : Math.ceil(readySeconds / 60) }),
    [modes, resolvedMode, readySeconds],
  );

  const status: CustomerOrderingStatus =
    current === null
      ? { kind: "loading" }
      : current.kind === "ready"
        ? menuPending
          ? { kind: "loading" }
          : refusedReference !== null
          ? { kind: "session-refused", correlationId: refusedReference }
          : // A flow with no fulfilment context cannot open a FOOD cart: refuse
            // rather than guess a location or a mode.
            built === null
          ? { kind: "failed", correlationId: null }
          : { kind: "ready" }
        : current;

  return {
    status,
    flow: wire?.flow,
    services: refusedReference === null ? (built ?? undefined) : undefined,
    catalogue: served?.catalogue ?? NO_CATALOGUE,
    cardConfig: built === null ? null : cardConfig,
    disclosure: wire?.disclosure ?? null,
    intake,
    checkout,
    storefront,
    placementRefusal,
  };
}
